/**
 * Pro entitlement — session + long-lived license JWT (cached offline ~120 days).
 * Production does not trust localStorage.spenderly_pro_unlock.
 */
import { apiFetch, apiConfigured } from './api';

const SESSION_KEY = 'spenderly_session';
const LICENSE_KEY = 'spenderly_pro_license';
const ACCOUNT_KEY = 'spenderly_pro_account';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function licenseStillValid(payload) {
  if (!payload || payload.plan !== 'pro') return false;
  if (payload.typ && payload.typ !== 'license' && payload.typ !== 'session') {
    // accept either typ for resilience
  }
  if (payload.exp && payload.exp * 1000 < Date.now()) return false;
  return payload.plan === 'pro';
}

export function getSessionToken() {
  try {
    return localStorage.getItem(SESSION_KEY) || '';
  } catch {
    return '';
  }
}

export function getLicenseToken() {
  try {
    return localStorage.getItem(LICENSE_KEY) || '';
  } catch {
    return '';
  }
}

export function getProAccount() {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveAuthBundle({ token, license, user } = {}) {
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    if (license) localStorage.setItem(LICENSE_KEY, license);
    if (user) {
      localStorage.setItem(ACCOUNT_KEY, JSON.stringify({
        id: user.id,
        email: user.email,
        name: user.name,
        plan: user.plan,
      }));
    }
  } catch { /* ignore quota */ }
}

export function clearAuthBundle() {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LICENSE_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
  } catch { /* ignore */ }
}

export function readLicensePayload() {
  return decodeJwtPayload(getLicenseToken());
}

/** Dev-only local unlock (never trusted in production builds). */
export function isDevProUnlock() {
  return process.env.NODE_ENV === 'development'
    && typeof localStorage !== 'undefined'
    && localStorage.getItem('spenderly_pro_unlock') === '1';
}

/**
 * Whether this device may use Pro features (history + vault).
 * @param {object|null} user — local guest/user from AppContext
 */
export function isProEntitled(user) {
  if (isDevProUnlock()) return true;
  if (user && user.plan === 'pro') return true;
  const account = getProAccount();
  if (account?.plan === 'pro' && licenseStillValid(readLicensePayload())) return true;
  return licenseStillValid(readLicensePayload());
}

/** Silent refresh when online; returns updated entitlement boolean. */
export async function refreshEntitlement() {
  if (!apiConfigured() || typeof navigator !== 'undefined' && !navigator.onLine) {
    return isProEntitled(null);
  }
  const session = getSessionToken();
  if (!session) return isProEntitled(null);
  try {
    const data = await apiFetch('/auth/entitlement', { token: session });
    if (data?.entitled && data.license) {
      saveAuthBundle({ license: data.license, user: { ...(getProAccount() || {}), plan: 'pro' } });
      return true;
    }
    if (data && data.entitled === false) {
      try {
        localStorage.removeItem(LICENSE_KEY);
        const acc = getProAccount();
        if (acc) localStorage.setItem(ACCOUNT_KEY, JSON.stringify({ ...acc, plan: 'free' }));
      } catch { /* ignore */ }
      return false;
    }
  } catch {
    // offline / cold API — keep cached license
  }
  return isProEntitled(null);
}

export async function fetchPricing() {
  if (!apiConfigured()) {
    return { amountInr: 499, early: true, currency: 'INR', label: 'Early bird lifetime' };
  }
  return apiFetch('/billing/pricing');
}
